import * as React from "react";

import Text from "app/components/Text";
import LogoSVG from "app/components/shared/LogoSVG";
import storageActions from "app/utils/storageActions";
import store from "app/redux/store";

import {
  View,
  StatusBar,
  Platform,
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  AppState,
} from "react-native";
import TouchableOpacity from "app/components/TouchableOpacity";

import {
  NavigationContainer,
  DefaultTheme,
  DarkTheme,
  CommonActions,
  StackActions,
  DrawerActions,
} from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import {
  createDrawerNavigator,
  DrawerContentScrollView,
  DrawerItemList,
  DrawerItem,
} from "@react-navigation/drawer";

import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  AntDesign,
  FontAwesome5,
  Fontisto,
  Entypo,
  Octicons,
  Foundation,
  FontAwesome,
  Feather,
} from "@expo/vector-icons";

import {
  KeyboardAccessoryNavigation,
  KeyboardAccessoryView,
} from "react-native-keyboard-accessory";

import prefActions from "app/utils/prefActions";
import alertActions from "app/utils/alertActions";

import { navigationRef } from "app/utils/RootNavigation";
import * as RootNavigation from "app/utils/RootNavigation";
import ENV from "app/utils/ENV";
import { styleSheet } from "app/utils/styleSheet";
import notificationHelpers from "app/utils/notificationHelpers";
import colorHelpers from "app/utils/colorHelpers";
import keyboardHelpers from "app/utils/keyboardHelpers";

import KeyboardShortcuts from "app/components/KeyboardShortcuts";
import About from "app/components/About";
import SettingsModal from "app/components/SettingsModal";
import NarrativeModal from "app/components/NarrativeModal";
import Toast from "app/components/Toast";

import TextView from "app/views/TextView";
import Network from "app/views/Network";
import Chapters from "app/views/Chapters";
import Chapter from "app/views/Chapter";
import Project from "app/views/Project";
import Intro from "app/views/Intro";
import Edit from "app/views/Edit";
import Create from "app/views/Create";
import IconTest from "app/views/IconTest";
import Settings from "app/views/Settings";
import Profile from "app/views/Profile";
import Waves from "app/views/Waves";
import Scratchpad from "app/views/Scratchpad";
import AdvancedSearch from "app/views/AdvancedSearch";
import Widget from "app/views/Widget";
import ToDo from "app/views/ToDo";
import LooseEnds from "app/views/LooseEnds";
import Atlas from "app/views/Atlas";
import Calendar from "app/views/Calendar";
import Value from "app/views/Value";

import HR from "app/components/shared/HR";

const Stack = createStackNavigator();
const Drawer = createDrawerNavigator();

import { StyleContext } from "app/contexts/Style";

export default class Router extends React.Component {
  static contextType = StyleContext;

  constructor(props, context) {
    super(props, context);
    let customTheme = this.customTheme(context);
    this.state = {
      showKeyboardShortcuts: false,
      showAbout: false,
      showSettings: false,
      customTheme,
      colorScheme: context.colorScheme,
      uiColor: context.uiColor,
      appState: AppState.currentState,
      lastThemeChangeAt: new Date().getTime(),
    };
  }

  componentDidMount() {
    this.subscribe();
    this.appStateListener = AppState.addEventListener(
      "change",
      this._handleAppStateChange,
    );
    if (ENV.WEB)
      document.addEventListener("keydown", keyboardHelpers.globalListener);
    keyboardHelpers.onToggleShortcuts = () =>
      this.setState((s) => ({ showKeyboardShortcuts: !s.showKeyboardShortcuts }));
  }

  _handleAppStateChange = (nextAppState) => {
    if (
      this.state.appState.match(/inactive|background/) &&
      nextAppState === "active"
    ) {
      if (new Date().getTime() - this.state.lastThemeChangeAt > 30 * 1000) {
        this.setState({
          customTheme: this.customTheme(),
          lastThemeChangeAt: new Date().getTime(),
        });
      }
    }
    this.setState({ appState: nextAppState });
  };

  componentWillUnmount() {
    this.unsubscribeRedux && this.unsubscribeRedux();
    this.appStateListener?.remove();
    if (ENV.WEB)
      document.removeEventListener("keydown", keyboardHelpers.globalListener);
    if (keyboardHelpers.onToggleShortcuts) keyboardHelpers.onToggleShortcuts = null;
  }

  subscribe = async () => {
    if (!!this.unsubscribeRedux) return;
    this.unsubscribeRedux = store.subscribe(() => {
      let { preferences } = store.getState();
      // console.log(this.context.colorScheme);
      if (
        this.context.colorScheme != this.state.colorScheme ||
        this.context.uiColor != this.state.uiColor
      ) {
        this.setState({
          colorScheme: this.context.colorScheme,
          uiColor: this.context.uiColor,
          customTheme: this.customTheme(),
        });
      }
    });
  };

  customTheme = (context = null) => {
    if (!context) context = this.context;
    let baseTheme = ENV.DARK ? DarkTheme : DefaultTheme;
    let c = context.colors;
    let headerColor = c.fill;
    let headerColorKey = null;
    if (context?.uiColor == "default") {
      headerColorKey = "default";
      headerColor = c.fill;
    } else if (context.colorScheme == "clarity") {
      let allColors = colorHelpers.allColors(context.colorScheme);
      if (!!context.uiColor) {
        headerColorKey = context.uiColor;
      } else {
        let exclude = ["cream", "white", "gray", "black"];
        let colorNames = Object.keys(allColors).filter(
          (n) => !exclude.includes(n),
        );
        headerColorKey =
          colorNames[Math.floor(Math.random() * colorNames.length)];
      }
      headerColor = allColors[headerColorKey];
    }
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        background: c.empty,
        card: headerColor,
        border: c.trim,
        border: "transparent",
        text: this.headerTintColor(context, headerColorKey, headerColor),
        primary: c.active,
      },
      headerColor,
      headerColorKey,
    };
  };

  render() {
    let { showKeyboardShortcuts, showAbout, showSettings } = this.state;

    let drawerColor = this.headerTintColor();
    let drawerActiveTintColor = drawerColor;
    let drawerInactiveTintColor = drawerColor;

    return (
      <View
        style={{
          flex: 1,
          backgroundColor: this.context.colors.empty,
          // backgroundColor: "red",
        }}
      >
        <NavigationContainer
          theme={this.state.customTheme}
          ref={navigationRef}
          linking={Linking}
          drawerPosition={ENV.WEB ? "left" : "right"}
        >
          <Drawer.Navigator
            initialRouteName={"Chapters"}
            drawerPosition={ENV.WEB ? "left" : "right"}
            drawerStyle={{
              // backgroundColor: '#c6cbef',
              width: 220,
            }}
            screenOptions={{
              //   gestureEnabled: false,
              headerShown: false,
              //   swipeEnabled: false
              ...this.headerOptions(),
            }}
            // swipeEnabled={false}
            // gestureEnabled={false}
            // headerShown={false}
            drawerContent={(props) => {
              let currentRoute = props.state.routeNames[props.state.index];
              return (
                <DrawerContentScrollView {...props}>
                  <DrawerItem
                    label="Mandelbrot"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    // activeBackgroundColor={drawerActiveBackgroundColor}
                    // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                    icon={({ focused, color, size }) => (
                      <Entypo
                        color={color}
                        size={size}
                        name={focused ? "home" : "home"}
                      />
                    )}
                    onPress={() => {
                      props.navigation.dispatch(StackActions.popToTop());
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <HR color={this.headerTintColor()} opacity={0.25} />

                  {/*<DrawerItemList {...props} />*/}

                  <DrawerItem
                    label="Chapters"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    // activeBackgroundColor={drawerActiveBackgroundColor}
                    // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                    focused={currentRoute == "Chapters"}
                    icon={({ focused, color, size }) => (
                      <Entypo color={color} size={size} name={"open-book"} />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Chapters", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Overview"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Overview"}
                    icon={({ focused, color, size }) => (
                      <Feather color={color} size={size} name={"list"} />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Overview", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Network"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Network"}
                    icon={({ focused, color, size }) => (
                      <FontAwesome5
                        color={color}
                        size={size}
                        name={"code-branch"}
                      />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Network", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Profile"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Profile"}
                    icon={({ focused, color, size }) => (
                      <Feather color={color} size={size} name={"user"} />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Profile", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Waves"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Waves"}
                    icon={({ focused, color, size }) => (
                      <MaterialCommunityIcons
                        color={color}
                        size={size}
                        name={"sine-wave"}
                      />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Waves", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Widget"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Widget"}
                    // icon={({ focused, color, size }) => (
                    //   <MaterialCommunityIcons
                    //     color={color}
                    //     size={size}
                    //     name={"sine-wave"}
                    //   />
                    // )}
                    onPress={(e) => {
                      RootNavigation.navigate("Widget", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="ToDo"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "ToDo"}
                    // icon={({ focused, color, size }) => (
                    //   <MaterialCommunityIcons
                    //     color={color}
                    //     size={size}
                    //     name={"sine-wave"}
                    //   />
                    // )}
                    onPress={(e) => {
                      RootNavigation.navigate("ToDo", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Loose Ends"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "LooseEnds"}
                    onPress={(e) => {
                      RootNavigation.navigate("LooseEnds", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Atlas"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Atlas"}
                    onPress={(e) => {
                      RootNavigation.navigate("Atlas", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <DrawerItem
                    label="Calendar"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    focused={currentRoute == "Calendar"}
                    icon={({ focused, color, size }) => (
                      <Feather color={color} size={size} name={"calendar"} />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Calendar", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  {ENV.WEB && (
                    <HR color={this.headerTintColor()} opacity={0.25} />
                  )}

                  <DrawerItem
                    label="Tutorial"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    // activeBackgroundColor={drawerActiveBackgroundColor}
                    // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                    icon={({ focused, color, size }) => (
                      <Entypo
                        color={color}
                        size={size}
                        name={"help-with-circle"}
                      />
                    )}
                    onPress={(e) => {
                      RootNavigation.navigate("Intro", {}, e);
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  {ENV.WEB && (
                    <DrawerItem
                      label="Shortcuts"
                      activeTintColor={drawerActiveTintColor}
                      inactiveTintColor={drawerInactiveTintColor}
                      // activeBackgroundColor={drawerActiveBackgroundColor}
                      // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                      icon={({ focused, color, size }) => (
                        <Entypo color={color} size={size} name={"keyboard"} />
                      )}
                      onPress={() => {
                        this.setState({ showKeyboardShortcuts: true });
                        props.navigation.dispatch(DrawerActions.closeDrawer());
                      }}
                    />
                  )}

                  <DrawerItem
                    label="Settings"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    // activeBackgroundColor={drawerActiveBackgroundColor}
                    // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                    focused={currentRoute == "Settings"}
                    icon={({ focused, color, size }) => (
                      <Octicons color={color} size={size} name={"gear"} />
                    )}
                    onPress={() => {
                      // RootNavigation.navigate("Settings");
                      this.setState({ showSettings: true });
                      props.navigation.dispatch(DrawerActions.closeDrawer());
                    }}
                  />

                  <HR color={this.headerTintColor()} opacity={0.25} />

                  <DrawerItem
                    label="Log Out"
                    activeTintColor={drawerActiveTintColor}
                    inactiveTintColor={drawerInactiveTintColor}
                    // activeBackgroundColor={drawerActiveBackgroundColor}
                    // inactiveBackgroundColor={drawerInactiveBackgroundColor}
                    icon={({ focused, color, size }) => (
                      <Entypo color={color} size={size} name={"log-out"} />
                    )}
                    onPress={async () => {
                      let confirm = await alertActions.confirm({
                        text: "Log Out",
                      });
                      if (confirm) {
                        await storageActions.clearAll();
                        this.props.clearToken();
                      }
                    }}
                  />
                </DrawerContentScrollView>
              );
            }}
          >
            <Drawer.Screen name="Chapters" component={this.ChaptersStack} />
            <Drawer.Screen name="Overview" component={this.TextViewStack} />
            <Drawer.Screen name="Network" component={this.NetworkStack} />
            <Drawer.Screen name="Profile" component={this.ProfileStack} />
            <Drawer.Screen name="Waves" component={this.WavesStack} />
            <Drawer.Screen name="Widget" component={this.WidgetStack} />
            <Drawer.Screen name="ToDo" component={this.ToDoStack} />
            <Drawer.Screen name="LooseEnds" component={this.LooseEndsStack} />
            <Drawer.Screen name="Atlas" component={this.AtlasStack} />
            <Drawer.Screen name="Calendar" component={this.CalendarStack} />
            <Drawer.Screen name="Scratchpad" component={this.ScratchpadStack} />
            <Drawer.Screen name="Settings" component={this.SettingsStack} />
            <Drawer.Screen name="IconTest" component={this.IconTestStack} />
          </Drawer.Navigator>
          <StatusBar barStyle="light-content" />
        </NavigationContainer>

        {showKeyboardShortcuts && (
          <KeyboardShortcuts
            onHide={() => this.setState({ showKeyboardShortcuts: false })}
          />
        )}
        {showAbout && (
          <About onHide={() => this.setState({ showAbout: false })} />
        )}
        {showSettings && (
          <SettingsModal
            onHide={() => this.setState({ showSettings: false })}
          />
        )}
        {!!this.context.narrativeChapter && (
          <NarrativeModal
            chapter={this.context.narrativeChapter}
            onClose={this.context.closeNarrative}
            onSaved={() => this.context.narrativeOnSaved?.()}
          />
        )}

        {!ENV.WEB && (
          <KeyboardAccessoryNavigation
            // alwaysVisible={false}
            // inSafeAreaView
            hideBorder
            style={{
              backgroundColor: this.context.colors.fill,
              minHeight: 70,
              padding: this.context.sizes.tiny,
              // justifyContent: "center",
            }}
            ref={(r) => (this.ref = r)}
            previousButton={this.renderLeftKeyboardAccButtons()}
            nextButton={() => null}
            doneButton={
              <View style={{ flexDirection: "row" }}>
                {(true || this.context.asdfasdf) && (
                  <Text
                    selectedButton
                    onPress={() => {
                      store.dispatch({
                        type: "listeners/event",
                        event: "meta_k",
                      });
                    }}
                  >
                    Strike
                  </Text>
                )}
                <Text selectedButton style={{ marginRight: 0 }}>
                  Done
                </Text>
              </View>
            }
          />
        )}
        <Toast />
      </View>
    );
  }

  renderLeftKeyboardAccButtons = () => {
    let { keyboardAccType, selectedText } = this.context;
    if (keyboardAccType == "scratchpad") {
      let hasSelection = !!selectedText?.length;
      if (hasSelection) {
        return (
          <View style={{ flexDirection: "row" }}>
            <Text
              disabled={!hasSelection}
              selectedButton //={hasSelection}
              onPress={() => {
                this.context.createFromSelection();
              }}
            >
              Create
            </Text>
          </View>
        );
      } else {
        return (
          <View style={{ flexDirection: "row" }}>
            <Text
              selectedButton
              style={{ marginRight: 0 }}
              onPress={() => {
                store.dispatch({
                  type: "listeners/event",
                  event: "tab",
                });
              }}
            >
              ⇥
            </Text>
          </View>
        );
      }
    } else {
      return (
        <View style={{ flexDirection: "row" }}>
          <Text
            selectedButton
            // style={{ marginRight: 0 }}
            onPress={() => {
              store.dispatch({
                type: "listeners/event",
                event: "arrow_up",
              });
            }}
          >
            <Feather name={`chevron-up`} />
          </Text>
          <Text
            selectedButton
            // style={{ marginRight: 0 }}
            onPress={() => {
              store.dispatch({
                type: "listeners/event",
                event: "arrow_down",
              });
            }}
          >
            <Feather name={`chevron-down`} />
          </Text>
          <Text
            selectedButton
            style={{ marginRight: 0 }}
            onPress={() => {
              store.dispatch({
                type: "listeners/event",
                event: "tab",
              });
            }}
          >
            ⇥
          </Text>
        </View>
      );
    }
  };

  headerOptions = () => {
    return {
      headerRight: this.headerRight,
      // headerLeft: this.headerLeft,
      headerTitle: this.headerLogo,
      cardStyle: { flex: 1 },
      drawerPosition: ENV.WEB ? "left" : "right",
      drawerStyle: {
        width: 220,
      },
    };
  };

  headerTintColor = (
    context = null,
    headerColorKey = null,
    headerColor = null,
  ) => {
    if (!headerColorKey) {
      headerColorKey = this?.state?.customTheme?.headerColorKey;
      headerColor = this?.state?.customTheme?.headerColor;
    }
    let colorScheme, colors;
    if (!!context) {
      colorScheme = context.colorScheme;
      colors = context.colors;
    } else {
      colorScheme = this.state.colorScheme;
      colors = this.context.colors;
    }
    let brightBG = [
      "yellow",
      "cyan",
      "viridian",
      "lime",
      "cream",
      "white",
      "gray",
      "default",
    ].includes(headerColorKey);
    if (colorScheme == "clarity" && brightBG) {
      return colorHelpers.modifyHex(headerColor, 0.6);
    }
    return colorScheme == "clarity"
      ? brightBG
        ? colors.darkForeground
        : colors.lightForeground
      : colors.foreground;
  };

  headerRight = () => {
    return (
      <TouchableOpacity onPress={RootNavigation.toggleDrawer}>
        <Ionicons
          name="menu"
          size={32}
          color={this.headerTintColor()}
          style={{ marginRight: 20 }}
        />
      </TouchableOpacity>
    );
  };

  headerLeft = () => {
    return (
      <TouchableOpacity onPress={RootNavigation.popToTop}>
        <View style={{ marginLeft: 26 }}>
          <LogoSVG color={this.headerTintColor()} />
        </View>
        {/*<Ionicons
          name="ios-menu"
          size={32}
          color={this.context.colors.foreground}
          style={{ marginLeft: 20 }}
        />*/}
      </TouchableOpacity>
    );
  };

  headerLogo = () => {
    return (
      <TouchableOpacity onPress={RootNavigation.popToTop}>
        <LogoSVG color={this.headerTintColor()} />
      </TouchableOpacity>
    );
  };

  ChaptersStack = () => {
    return (
      <Stack.Navigator
        screenOptions={{
          ...this.headerOptions(),
        }}
        initialRouteName={!!this.props.intro ? "Intro" : "Chapters"}
      >
        <Stack.Screen name="Chapters" component={Chapters} />
        <Stack.Screen name="Chapter" component={Chapter} />
        <Stack.Screen name="Intro" component={Intro} />
        <Drawer.Screen
          name="Project"
          component={Project}
        />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Create" component={Create} />
        <Drawer.Screen name="Network" component={Network} />
        <Drawer.Screen name="AdvancedSearch" component={AdvancedSearch} />
      </Stack.Navigator>
    );
  };

  ProfileStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Profile" component={Profile} />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  WavesStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Waves" component={Waves} />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  WidgetStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Widget" component={Widget} />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  ToDoStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="ToDo" component={ToDo} />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  LooseEndsStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="LooseEnds" component={LooseEnds} />
        <Stack.Screen name="Value" component={Value} />
        <Stack.Screen name="Project" component={Project} />
        <Stack.Screen name="Edit" component={Edit} />
        <Stack.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  AtlasStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Atlas" component={Atlas} />
        <Stack.Screen name="Value" component={Value} />
        <Stack.Screen name="Project" component={Project} />
        <Stack.Screen name="Edit" component={Edit} />
        <Stack.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  CalendarStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Calendar" component={Calendar} />
        <Stack.Screen name="Chapters" component={Chapters} />
        <Stack.Screen name="Chapter" component={Chapter} />
        <Stack.Screen name="Value" component={Value} />
        <Stack.Screen name="Edit" component={Edit} />
        <Stack.Screen name="Create" component={Create} />
      </Stack.Navigator>
    );
  };

  ScratchpadStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Scratchpad" component={Scratchpad} />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Chapter" component={Chapter} />
      </Stack.Navigator>
    );
  };

  TextViewStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen
          name="TextView"
          component={TextView}
          options={{
            headerTitle: "Overview",
          }}
        />
        <Drawer.Screen name="Project" component={Project} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Create" component={Create} />
      </Stack.Navigator>
    );
  };

  NetworkStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen
          name="Network"
          component={Network}
          options={{
            headerTitle: "Network",
          }}
        />
        <Drawer.Screen name="Project" component={Project} />
        <Stack.Screen name="Chapters" component={Chapters} />
        <Stack.Screen name="Chapter" component={Chapter} />
        <Drawer.Screen name="Edit" component={Edit} />
        <Drawer.Screen name="Create" component={Create} />
      </Stack.Navigator>
    );
  };

  IconTestStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="IconTest" component={IconTest} />
      </Stack.Navigator>
    );
  };

  SettingsStack = () => {
    return (
      <Stack.Navigator screenOptions={{ ...this.headerOptions() }}>
        <Stack.Screen name="Settings" component={Settings} />
      </Stack.Navigator>
    );
  };
}

const Linking = {
  // prefixes: ['https://mychat.com', 'mychat://'],
  enabled: true,
  // config: {
  //   screens: {
  //     // Overview: '',
  //     Profile: ':id/profile',
  //     Settings: ':id/blog',
  //     // Value: ':id/value',
  //   }
  // },
};
